import { FC } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router';

import { useChatCategoryStore } from '../../hooks/useChatCategoryStore';
import { useAppStore } from '../../hooks/useAppStore';
import { useOrgLinkBranding } from '../../contexts/AuthContext';
import { DocumentIframeViewer } from './DocumentIframeViewer';
import { GradientButton } from './core/GradientButton';
import { AskReveChatIcon } from '../icons/AskReveChatIcon';

export const ContractPreview: FC = observer(() => {
    const { contractToPreview, setContractToPreview } = useAppStore();
    const { setIsAskingReveForContract } = useChatCategoryStore();
    const { askReveLabel } = useOrgLinkBranding();
    // setIsAskingReveForContract only drives ContractResearch's own inline welcome
    // flow, which isn't mounted outside /contract-research (e.g. this same preview
    // renders in the chat side panel on /channels/:id) — there it would be a silent
    // no-op, so only show the button where it actually does something. Checked via
    // the route itself (not ChatStore.isContractResearchChannel/channel), because
    // channel is only reset by resetAskReveal() for claim chats — it stays
    // "contract-research" after the Ask Reve redirect to /channels/:id, which would
    // make isContractResearchChannel (and this button) a no-op there too.
    const location = useLocation();
    const isContractResearchRoute = location.pathname.includes('/contract-research');

    const handleCloseDocument = () => {
        setContractToPreview(null);
    };

    if (!contractToPreview) {
        return null;
    }

    // Show document preview for the selected contract
    return (
        <Box
            sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', borderLeft: '1px solid', borderColor: 'divider', overflow: 'hidden', position: 'relative' }}
        >
            <Box sx={{ height: '100%', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <DocumentIframeViewer
                    documentId={contractToPreview.document_id}
                    documentName={contractToPreview.title}
                    onClose={handleCloseDocument}
                />
            </Box>

            {isContractResearchRoute ? (
                <Box sx={{ position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 10, pointerEvents: 'none', display: 'flex' }}>
                    <GradientButton
                        onClick={() => setIsAskingReveForContract(true)}
                        startIcon={<AskReveChatIcon />}
                        sx={{
                            pointerEvents: 'auto',
                            fontWeight: 500,
                            borderRadius: 999,
                            boxShadow: 4,
                            px: 2,
                            py: 1,
                        }}
                    >
                        {askReveLabel}
                    </GradientButton>
                </Box>
            ) : null}
        </Box>
    );
});
